import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { Wallet, TrendingUp, Coins, PiggyBank, Plus, ArrowUpRight, ArrowDownLeft, Search, Loader2, Bell, X, Star } from "lucide-react";
import { StatCard, Panel, GainBadge, Skeleton, EmptyState, Segmented } from "../components/ui";
import { BuyDialog, SellDialog, AlertDialog } from "../components/Dialogs";
import InstrumentLogo from "../components/InstrumentLogo";
import { api } from "../lib/api";
import { pct, shortDate, gainColor, money, isCryptoType } from "../lib/format";
import { timeAxis } from "../lib/chartAxis";
import { useMoney } from "../context/MoneyContext";
import { useTranslation } from "react-i18next";
import useSeo from "../lib/useSeo";
import useRefreshOnReturn from "../lib/useRefreshOnReturn";
import { toast } from "sonner";

const PERIODS = [
  { value: "1w", label: "1W" }, { value: "1m", label: "1M" },
  { value: "1y", label: "1Y" }, { value: "5y", label: "5Y" }, { value: "all", label: "All" },
];

export default function Dashboard() {
  const { t } = useTranslation();
  const { display } = useMoney();
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  // 1W and 1M come back as intraday points (one per 15 or 60-minute candle).
  const [seriesIntraday, setSeriesIntraday] = useState(false);
  const takeSeries = (r) => { setSeries(r.data.series); setSeriesIntraday(!!r.data.intraday); };
  const [period, setPeriod] = useState("1y");
  const [loading, setLoading] = useState(true);
  const [buyOpen, setBuyOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);

  // Private, per-user data — must never be indexed.
  useSeo({ title: t("dashboard.title"), noindex: true });

  // Watchlist — folded in here instead of its own page, since it was showing
  // largely the same instruments as Holdings/Analytics.
  const [watchItems, setWatchItems] = useState([]);
  const [watchLoading, setWatchLoading] = useState(true);
  const [wq, setWq] = useState("");
  const [wResults, setWResults] = useState([]);
  const [wSearching, setWSearching] = useState(false);
  const [buy, setBuy] = useState({ open: false, ticker: "", name: "" });
  const [alert, setAlert] = useState({ open: false, ticker: "", name: "" });

  // `silent` skips the skeleton: a background refresh should update the numbers
  // in place, not blank the page the user is already reading.
  const loadSummary = (silent) => {
    if (!silent) setLoading(true);
    api.analyticsSummary().then((r) => setSummary(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { loadSummary(); }, []);
  useEffect(() => {
    api.portfolioTimeseries(period).then(takeSeries).catch(() => setSeries([]));
  }, [period]);

  const loadWatchlist = (silent) => {
    if (!silent) setWatchLoading(true);
    api.watchlist().then((r) => setWatchItems(r.data)).catch(() => {}).finally(() => setWatchLoading(false));
  };
  useEffect(() => { loadWatchlist(); }, []);

  // Everything on this page is live market data; coming back to the app refetches
  // all of it rather than leaving yesterday's valuation on screen.
  useRefreshOnReturn(() => {
    loadSummary(true);
    loadWatchlist(true);
    api.portfolioTimeseries(period).then(takeSeries).catch(() => {});
  });
  useEffect(() => {
    if (!wq.trim()) { setWResults([]); return; }
    setWSearching(true);
    const timer = setTimeout(async () => {
      try { const r = await api.search(wq); setWResults(r.data); } catch {} finally { setWSearching(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [wq]);

  const watched = new Set(watchItems.map((i) => i.ticker));
  const addWatch = async (r) => {
    try { await api.addWatch({ ticker: r.ticker, name: r.name, asset_type: r.asset_type }); toast.success(`${r.ticker} added`); loadWatchlist(); }
    catch { toast.error("Failed to add"); }
  };
  const removeWatch = async (ticker) => {
    try { await api.removeWatch(ticker); toast.success(`${ticker} removed`); loadWatchlist(); }
    catch { toast.error("Failed to remove"); }
  };

  const hasData = summary && summary.holdings_count > 0;
  const seriesAxis = timeAxis(series, seriesIntraday, { dailyTick: shortDate });

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="label">{t("dashboard.overview")}</span>
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">{t("dashboard.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          {/* Sell only appears once there is something to sell — an empty portfolio
              would otherwise offer an action that can only end in an error. */}
          {hasData && (
            <button data-testid="dashboard-sell-btn" className="btn-outline" onClick={() => setSellOpen(true)}>
              <ArrowDownLeft size={18} /> {t("common.recordSell")}
            </button>
          )}
          <button data-testid="dashboard-buy-btn" className="btn-primary" onClick={() => setBuyOpen(true)}>
            <Plus size={18} /> {t("common.recordBuy")}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-36" />)}
        </div>
      ) : !hasData ? (
        <EmptyState
          testid="dashboard-empty"
          title={t("dashboard.emptyTitle")}
          description={t("dashboard.emptyDesc")}
          action={<button className="btn-primary" onClick={() => setBuyOpen(true)}><Plus size={18} /> {t("dashboard.emptyAction")}</button>}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            <StatCard testid="stat-total-value" label={t("dashboard.portfolioValue")} icon={Wallet} value={display(summary.total_value_usd).primary}
              secondary={display(summary.total_value_usd).secondary} sub={t("dashboard.costBasis", { value: display(summary.total_cost_usd).primary })} delay={0} />
            <StatCard testid="stat-unrealized" label={t("dashboard.unrealizedPL")} icon={TrendingUp} value={display(summary.unrealized_gain_usd).primary}
              secondary={display(summary.unrealized_gain_usd).secondary} sub={pct(summary.unrealized_gain_pct)} subColor={gainColor(summary.unrealized_gain_usd)} delay={0.05} />
            <StatCard testid="stat-realized" label={t("dashboard.realizedPL")} icon={Coins} value={display(summary.realized_gain_usd).primary}
              secondary={display(summary.realized_gain_usd).secondary} sub={t("dashboard.fromClosed")} subColor={gainColor(summary.realized_gain_usd)} delay={0.1} />
            <StatCard testid="stat-dividends" label={t("dashboard.dividendIncome")} icon={PiggyBank} value={display(summary.dividend_income_usd).primary}
              secondary={display(summary.dividend_income_usd).secondary} sub={t("dashboard.allTime")} delay={0.15} />
          </div>

          <Panel delay={0.1} className="card p-6" testid="portfolio-chart-panel">
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div>
                <span className="label">{t("dashboard.portfolioValue")}</span>
                <div className="text-2xl font-heading font-semibold tracking-tight mt-1 tabular">{display(summary.total_value_usd).primary}</div>
                {display(summary.total_value_usd).secondary && <div className="text-sm tabular text-slate-400">≈ {display(summary.total_value_usd).secondary}</div>}
              </div>
              <Segmented options={PERIODS} value={period} onChange={setPeriod} testid="dashboard-period" />
            </div>
            {series.length === 0 ? (
              <div className="h-[300px] flex items-center justify-center text-slate-400 text-sm">{t("analytics.notEnoughHistory")}</div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={series} margin={{ top: 6, right: 6, left: 4, bottom: 0 }}>
                  <defs>
                    <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0052FF" stopOpacity={0.2} />
                      <stop offset="100%" stopColor="#0052FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" {...seriesAxis.axis} tick={{ fontSize: 11, fill: "#94a3b8" }} minTickGap={50} axisLine={false} tickLine={false} />
                  {/* Fitted to the data, as the instrument chart is. From zero, a week's
                      ±2% is a flat line along the top of the chart. */}
                  <YAxis domain={["auto", "auto"]} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => display(v).primary} />
                  <Tooltip formatter={(v) => [display(v).primary, "Value"]} {...seriesAxis.tooltip} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Area type="linear" dataKey="value_usd" stroke="#0052FF" strokeWidth={2} fill="url(#pv)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Panel>

          <Panel delay={0.15} className="card p-6" testid="top-holdings-panel">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-semibold tracking-tight text-lg">{t("dashboard.topHoldings")}</h3>
              <Link to="/holdings" className="text-sm text-brand-600 hover:text-brand-700 flex items-center gap-1">
                {t("common.viewAll")} <ArrowUpRight size={15} />
              </Link>
            </div>
            <div className="space-y-1">
              {summary.instruments.slice(0, 6).map((h) => (
                <Link key={h.ticker} to={`/instrument/${h.ticker}`} data-testid={`dash-holding-${h.ticker}`}
                  className="flex items-center justify-between gap-3 py-3 px-2 rounded-xl hover:bg-slate-50 transition-colors">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <InstrumentLogo logo={h.logo} ticker={h.ticker} size={40} rounded="rounded-xl" />
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900">{h.ticker}</div>
                      <div className="text-xs text-slate-400 truncate">{h.name}</div>
                    </div>
                  </div>
                  <div className="text-right flex items-center gap-2 sm:gap-4 shrink-0">
                    <div>
                      <div className="tabular font-medium">{display(h.market_value_usd).primary}</div>
                      <div className="text-xs text-slate-400 tabular">{h.allocation_pct}{t("dashboard.ofBook")}</div>
                      {display(h.market_value_usd).secondary && <div className="text-xs text-slate-400 tabular">≈ {display(h.market_value_usd).secondary}</div>}
                    </div>
                    <GainBadge value={h.gain_pct} />
                  </div>
                </Link>
              ))}
            </div>
          </Panel>
        </>
      )}

      <Panel delay={0.2} className="card p-6" testid="dashboard-watchlist-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold tracking-tight text-lg">{t("watchlist.title")}</h3>
        </div>
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input data-testid="watchlist-search-input" value={wq} onChange={(e) => setWq(e.target.value)}
            placeholder={t("watchlist.searchAdd")} className="input pl-10 pr-10 py-2.5 text-sm" />
          {wSearching && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 text-brand-500 animate-spin" size={16} />}
        </div>
        <AnimatePresence>
          {wResults.length > 0 && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
              <div className="mt-2 space-y-1">
                {wResults.map((r) => (
                  <div key={r.ticker} data-testid={`wl-search-${r.ticker}`} className="flex items-center justify-between py-2 px-3 rounded-xl hover:bg-slate-50">
                    <Link to={`/instrument/${r.ticker}`} className="flex-1">
                      <span className="font-medium text-slate-900">{r.ticker}</span>
                      <span className="text-sm text-slate-400 ml-3">{r.name}</span>
                    </Link>
                    {watched.has(r.ticker) ? (
                      <span className="pill bg-emerald-50 text-emerald-600 flex items-center gap-1"><Star size={12} className="fill-emerald-500 text-emerald-500" /> {t("instrument.watching")}</span>
                    ) : (
                      <button data-testid={`wl-add-${r.ticker}`} onClick={() => addWatch(r)} className="btn-primary py-1.5 px-3 text-sm"><Plus size={14} /> {t("instrument.watch")}</button>
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {watchLoading ? (
          <div className="space-y-2 mt-4">{[0, 1].map((i) => <Skeleton key={i} className="h-14" />)}</div>
        ) : watchItems.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-6 mt-2">{t("watchlist.emptyDesc")}</div>
        ) : (
          <div className="mt-2 space-y-1">
            {watchItems.map((it) => (
              <div key={it.ticker} data-testid={`watch-row-${it.ticker}`} className="flex items-center justify-between gap-3 py-3 px-2 rounded-xl hover:bg-slate-50 transition-colors group">
                <Link to={`/instrument/${it.ticker}`} className="flex items-center gap-3 min-w-0 flex-1">
                  <InstrumentLogo logo={it.logo} ticker={it.ticker} size={36} rounded="rounded-xl" />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900">{it.ticker}</div>
                    <div className="text-xs text-slate-400 truncate">{it.name}</div>
                  </div>
                </Link>
                <div className="text-right shrink-0">
                  <div className="tabular font-medium">{it.price_usd != null ? display(it.price_usd).primary
                    : it.price ? money(it.price, it.currency, { crypto: isCryptoType(it.asset_type) }) : "—"}</div>
                  <GainBadge value={it.change_pct || 0} />
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`wl-buy-${it.ticker}`} onClick={() => setBuy({ open: true, ticker: it.ticker, name: it.name })} className="text-slate-300 hover:text-brand-600 transition-colors p-1.5"><Plus size={16} /></button>
                  <button data-testid={`wl-alert-${it.ticker}`} onClick={() => setAlert({ open: true, ticker: it.ticker, name: it.name })} className="text-slate-300 hover:text-brand-600 transition-colors p-1.5"><Bell size={15} /></button>
                  <button data-testid={`wl-remove-${it.ticker}`} onClick={() => removeWatch(it.ticker)} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity p-1.5"><X size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      <BuyDialog open={buyOpen} onClose={() => setBuyOpen(false)} onDone={loadSummary} />
      <SellDialog open={sellOpen} onClose={() => setSellOpen(false)} onDone={loadSummary} />
      <BuyDialog open={buy.open} ticker={buy.ticker} name={buy.name} onClose={() => setBuy({ open: false, ticker: "", name: "" })} onDone={loadWatchlist} />
      <AlertDialog open={alert.open} ticker={alert.ticker} name={alert.name} onClose={() => setAlert({ open: false, ticker: "", name: "" })} />
    </div>
  );
}
